import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useStore } from "../context/Store";
import { t } from "../i18n";
import { CheckCircle2, Camera, Truck, Flower2, Copy, Repeat, Gift } from "lucide-react";
import { fmtDate } from "../components/DateField";
import Seo from "../components/Seo";
import { trackPurchase } from "../components/MarketingLoader";
import { ExtrasUpsell, ExtrasReturn } from "../components/ExtrasUpsell";
import { api as apiClient } from "../lib/api";

export default function Confirmed() {
  const { locale = "en" } = useParams();
  const [params] = useSearchParams();
  const { clearCart } = useStore();
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);
  const [pwd, setPwd] = useState("");
  const [name, setName] = useState("");
  const [signedUp, setSignedUp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [subOpts, setSubOpts] = useState(null);
  useEffect(() => {
    api.get("/subscription-options").then((r) => setSubOpts(r.data)).catch(() => {});
  }, []);
  const rawSession = params.get("session_id");
  const sessionId = rawSession && !rawSession.includes("{") ? rawSession : params.get("tryb_session");
  const extrasSession = params.get("extras_session");

  useEffect(() => {
    if (!sessionId || tries > 12) return undefined;
    const id = setTimeout(() => {
      api.get(`/payments/status/${sessionId}`)
        .then((r) => {
          setStatus(r.data);
          if (r.data.payment_status === "paid") {
            clearCart();
            localStorage.removeItem("fl_last_order");
            apiClient.get("/public/site-config").then((c) => {
              trackPurchase(r.data, {
                conversion_id: c.data.marketing?.google_ads_conversion_id,
                label: c.data.marketing?.google_ads_purchase_label,
              });
            }).catch(() => trackPurchase(r.data, {}));
          }
          else setTries((v) => v + 1);
        })
        .catch(() => setTries((v) => v + 1));
    }, tries === 0 ? 300 : 2000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tries]);

  const paid = status?.payment_status === "paid";
  const shareUrl = `${window.location.origin}/${locale}?code=FIRST10`;

  const signUp = async () => {
    setBusy("account");
    setError("");
    try {
      await api.post("/auth/register", { email: status.buyer_email, password: pwd, name: name || status.buyer_email.split("@")[0], locale, order_number: status.order_number });
      setSignedUp(true);
    } catch (e) {
      setError(errText(e));
    }
    setBusy("");
  };

  const makeItAPlan = async (plan) => {
    setBusy(plan);
    setError("");
    try {
      const { data } = await api.post(`/orders/${status.order_number}/plan`, {
        buyer_email: status.buyer_email, plan, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(errText(e));
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white" data-testid="confirmation-page">
      <Seo title={`${t(locale, "thankYouTitle")} | Fleurinelle`} noindex />
      <div className="w-full lg:w-[55%] ps-5 pe-5 lg:ps-[8%] lg:pe-14 py-12 lg:py-20 space-y-10">
        <Link to={`/${locale}`} className="font-display text-2xl text-ink block">Fleurinelle</Link>

        <header className="space-y-3">
          <CheckCircle2 size={40} className={paid || extrasSession ? "text-success" : "text-brass animate-pulse"} />
          <h1 className="font-display text-3xl lg:text-4xl text-ink">
            {paid || extrasSession ? t(locale, "thankYouTitle") : `${t(locale, "loading")}…`}
          </h1>
          {paid ? (
            <p className="text-sm text-muted" data-testid="confirmation-subtitle">
              {t(locale, "thankYouSub").replace("{email}", status.buyer_email || "")}
            </p>
          ) : null}
        </header>

        {status ? (
          <div className="card p-6 space-y-4 text-sm" data-testid="order-confirmation">
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="smallcaps">{t(locale, "orderNumber")}</p>
                <p data-testid="confirmation-order-number" className="font-display text-2xl text-ink">{status.order_number}</p>
              </div>
              <p className="text-xs text-muted" data-testid="confirmation-status">{status.payment_status}</p>
            </div>
            {status.deliveries?.length ? (
              <ul className="space-y-1 text-xs text-muted border-t border-brass-light pt-3" data-testid="confirmation-deliveries">
                {status.deliveries.map((d) => (
                  <li key={d.order_number} className="tabular">{d.order_number} · {d.recipient_name} · {fmtDate(d.delivery_date)}</li>
                ))}
              </ul>
            ) : null}
            {status.recurring ? (
              <div className="border-t border-brass-light pt-3 text-xs text-muted space-y-1" data-testid="confirmation-subscription">
                <p className="text-ink text-sm flex items-center gap-2"><Repeat size={13} className="text-brass" />{t(locale, "mySubscriptions")} · {status.plan}</p>
                {status.next_delivery_date ? <p className="tabular">{t(locale, "nextDelivery")}: {fmtDate(status.next_delivery_date)}</p> : null}
                <p>{t(locale, "planPriceLocked")}</p>
              </div>
            ) : null}
          </div>
        ) : null}

        {extrasSession ? <ExtrasReturn locale={locale} sessionId={extrasSession} /> : null}

        {paid ? (
          <ExtrasUpsell locale={locale} orderNumber={status.order_number} email={status.buyer_email}
                        currency={status.currency} />
        ) : null}

        <section className="space-y-4">
          <h2 className="smallcaps">{t(locale, "whatHappensNext")}</h2>
          <ol className="space-y-4 text-sm text-charcoal">
            {[[Flower2, "step2Body"], [Truck, "step3Body"], [Camera, "proofBody"]].map(([Icon, key]) => (
              <li key={key} className="flex gap-3 items-start">
                <Icon size={16} className="text-brass mt-0.5 shrink-0" />
                <span>{t(locale, key)}</span>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link data-testid="confirmation-track-link" className="btn-primary"
                  to={`/${locale}/track?order=${status?.order_number || ""}&email=${status?.buyer_email || ""}`}>
              {t(locale, "trackOrder")}
            </Link>
            <a href="https://wa.me/212600000000" className="btn-ghost">{t(locale, "support")}</a>
          </div>
        </section>

        {error ? <p className="text-danger text-sm" data-testid="confirmation-error">{error}</p> : null}
      </div>

      <aside className="w-full lg:w-[45%] bg-plaster border-s border-brass-light ps-5 pe-5 lg:ps-14 lg:pe-[8%] py-12 lg:py-20 space-y-6"
             data-testid="confirmation-growth">
        {paid && !status?.recurring && subOpts?.enabled ? (
          <div className="card p-6 space-y-3" data-testid="plan-upsell">
            <h2 className="font-display text-xl text-ink flex items-center gap-2"><Repeat size={16} className="text-brass" />{t(locale, "upsellTitle")}</h2>
            <p className="text-sm text-charcoal">{t(locale, "upsellBody").replace("{discount}", subOpts?.discount_percent || 0)}</p>
            <button className="btn-primary w-full" data-testid="upsell-monthly" disabled={!!busy} onClick={() => makeItAPlan("monthly")}>
              {busy === "monthly" ? "…" : t(locale, "upsellCta")}
            </button>
            <div className="flex gap-3">
              <button className="btn-ghost text-xs" data-testid="upsell-weekly" disabled={!!busy} onClick={() => makeItAPlan("weekly")}>{t(locale, "upsellWeekly")}</button>
              <button className="btn-ghost text-xs" data-testid="upsell-yearly" disabled={!!busy} onClick={() => makeItAPlan("yearly")}>{t(locale, "upsellYearly")}</button>
            </div>
          </div>
        ) : null}

        {paid && !status?.has_account && !signedUp ? (
          <div className="card p-6 space-y-3" data-testid="confirmation-account-prompt">
            <h2 className="font-display text-xl text-ink">{t(locale, "createAccountTitle")}</h2>
            <p className="text-sm text-charcoal">{t(locale, "createAccountPrompt")}</p>
            <input className="field" data-testid="signup-name" placeholder={t(locale, "fullName")} value={name} onChange={(e) => setName(e.target.value)} />
            <input className="field" data-testid="signup-password" type="password" autoComplete="new-password"
                   placeholder={t(locale, "password")} value={pwd} onChange={(e) => setPwd(e.target.value)} />
            <button className="btn-primary w-full" data-testid="confirmation-create-account" disabled={pwd.length < 8 || busy === "account"} onClick={signUp}>
              {busy === "account" ? "…" : t(locale, "signUp")}
            </button>
          </div>
        ) : null}

        {signedUp ? (
          <div className="card p-6 space-y-2" data-testid="signup-success">
            <p className="text-sm text-success">{t(locale, "createAccountTitle")}</p>
            <Link className="btn-ghost inline-block" to={`/${locale}/account`}>{t(locale, "myOrders")}</Link>
          </div>
        ) : null}

        {paid ? (
          <div className="card p-6 space-y-3" data-testid="referral-card">
            <h2 className="font-display text-xl text-ink flex items-center gap-2"><Gift size={16} className="text-brass" />{t(locale, "shareTitle")}</h2>
            <p className="text-sm text-charcoal">{t(locale, "shareBody")}</p>
            <div className="flex gap-2">
              <input className="field flex-1 text-xs" readOnly value={shareUrl} data-testid="referral-link" />
              <button className="btn-ghost px-4 inline-flex items-center gap-1" data-testid="copy-referral"
                      onClick={() => { navigator.clipboard?.writeText(shareUrl); setCopied(true); }}>
                <Copy size={13} /> {copied ? t(locale, "copied") : t(locale, "copyLink")}
              </button>
            </div>
          </div>
        ) : null}

        {paid ? (
          <Link to={`/${locale}`} className="btn-ghost inline-block" data-testid="send-another">{t(locale, "sendAnother")}</Link>
        ) : null}
      </aside>
    </div>
  );
}
